import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

function call<TIn, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => {
    const fn = httpsCallable<TIn, TOut>(functions, name);
    const res = await fn(data);
    return res.data;
  };
}

// Auth
export const verifyClient = call<unknown, {
  ok: boolean;
  reason?: "not-found";
  clientId?: string;
  role?: "client";
}>("verifyClient");

// Stripe
export const createCheckoutSession = call<
  { serviceId: string; successPath?: string; cancelPath?: string },
  { url: string; id: string }
>("createCheckoutSession");

/**
 * Public-flow signup. Caller must already be signed in via Firebase phone OTP
 * (the OTP itself creates the auth user). This callable creates the clients
 * doc, Stripe customer, sets custom claims, and starts the Stripe charge —
 * returning the same shape as createPaymentIntentForService so the FE can
 * reuse the inline-checkout PaymentElement code.
 */
export const signupAndAssignService = call<
  {
    serviceId: string;
    priceOptionId?: string | null;
    companyName: string;
    contactName: string;
    email: string;
    phone: string;
  },
  {
    ok: boolean;
    type: "subscription" | "payment";
    clientSecret: string;
    resourceId: string;
    clientServiceId: string;
    clientId: string;
    service: {
      serviceId: string;
      title: string;
      priceDisplay: string;
      serviceType: string;
      unitAmount: number | null;
      currency: string | null;
      interval: "day" | "week" | "month" | "year" | null;
    };
  }
>("signupAndAssignService");

export const createPaymentIntentForService = call<
  {
    serviceId: string;
    clientServiceId?: string;
    /** Required when the service has 2+ price options and the pending row hasn't already locked one in. */
    priceOptionId?: string | null;
  },
  {
    type: "subscription" | "payment";
    clientSecret: string;
    resourceId: string;
    service: {
      serviceId: string;
      title: string;
      priceDisplay: string;
      serviceType: string;
      unitAmount: number | null;
      currency: string | null;
      interval: "day" | "week" | "month" | "year" | null;
    };
    customer: { stripeCustomerId: string; email: string | null };
  }
>("createPaymentIntentForService");

// Stripe – client self-service (native UI replacements for the hosted Customer Portal)
export const getMyBillingOverview = call<
  { limit?: number },
  {
    defaultPaymentMethod: {
      id: string;
      brand: string | null;
      last4: string | null;
      expMonth: number | null;
      expYear: number | null;
    } | null;
    subscriptions: Array<{
      id: string;
      status: string;
      currentPeriodEnd: number;
      cancelAtPeriodEnd: boolean;
      productName: string | null;
      unitAmount: number | null;
      currency: string | null;
      interval: string | null;
      quantity: number;
    }>;
    invoices: Array<{
      id: string;
      number: string | null;
      status: string | null;
      amountDue: number;
      amountPaid: number;
      currency: string;
      created: number;
      dueDate: number | null;
      hostedInvoiceUrl: string | null;
      invoicePdf: string | null;
      description: string | null;
    }>;
    charges: Array<{
      id: string;
      status: string;
      amount: number;
      amountRefunded: number;
      currency: string;
      created: number;
      description: string | null;
      paid: boolean;
      refunded: boolean;
      receiptUrl: string | null;
      paymentMethodBrand: string | null;
      paymentMethodLast4: string | null;
    }>;
  }
>("getMyBillingOverview");

export const createSetupIntent = call<
  Record<string, never>,
  { clientSecret: string; setupIntentId: string }
>("createSetupIntent");

export const setDefaultPaymentMethod = call<
  { paymentMethodId: string },
  { ok: true }
>("setDefaultPaymentMethod");

export const cancelMySubscription = call<
  { subscriptionId: string },
  { ok: true; cancelAtPeriodEnd: boolean; currentPeriodEnd: number }
>("cancelMySubscription");

export const getInvoicePaymentSheet = call<
  { invoiceId: string },
  {
    clientSecret: string;
    paymentIntentId: string;
    amountDue: number;
    currency: string;
  }
>("getInvoicePaymentSheet");

export const resumeMySubscription = call<
  { subscriptionId: string },
  { ok: true; cancelAtPeriodEnd: boolean }
>("resumeMySubscription");

// Admin – cancel actions
export const adminCancelSubscription = call<
  { clientId: string; subscriptionId: string; immediate?: boolean },
  | {
      ok: true;
      immediate: true;
      status: string;
    }
  | {
      ok: true;
      immediate: false;
      cancelAtPeriodEnd: boolean;
      currentPeriodEnd: number;
    }
>("adminCancelSubscription");

export const adminResumeSubscription = call<
  { clientId: string; subscriptionId: string },
  { ok: true; cancelAtPeriodEnd: boolean }
>("adminResumeSubscription");

export const adminCancelInvoice = call<
  { clientId: string; invoiceId: string },
  {
    ok: true;
    action: "deleted" | "voided" | "already_void" | "already_uncollectible";
    status?: string | null;
  }
>("adminCancelInvoice");

// Admin – billing overview
export const adminGetClientBillingOverview = call<
  { clientId: string; limit?: number },
  {
    client: Record<string, unknown>;
    stripeLinked: boolean;
    subscriptions: Array<{
      id: string;
      status: string;
      currentPeriodEnd: number;
      cancelAtPeriodEnd: boolean;
      productName: string | null;
      priceId: string | null;
      unitAmount: number | null;
      currency: string | null;
      interval: string | null;
      quantity: number;
    }>;
    invoices: Array<{
      id: string;
      number: string | null;
      status: string | null;
      amountDue: number;
      amountPaid: number;
      currency: string;
      created: number;
      dueDate: number | null;
      hostedInvoiceUrl: string | null;
      invoicePdf: string | null;
      description: string | null;
      subscription: string | null;
    }>;
    charges: Array<{
      id: string;
      status: string;
      amount: number;
      amountRefunded: number;
      currency: string;
      created: number;
      description: string | null;
      paid: boolean;
      refunded: boolean;
      receiptUrl: string | null;
      paymentMethodBrand: string | null;
      paymentMethodLast4: string | null;
    }>;
  }
>("getClientBillingOverview");

// Admin – clients
export const adminCreateClient = call<
  {
    companyName: string;
    contactName: string;
    phone: string;
    email?: string;
    websiteUrl?: string;
    stripeCustomerId?: string;
    createInStripe?: boolean;
  },
  { clientId: string }
>("createClient");

export const adminUpdateClient = call<
  {
    clientId: string;
    patch: {
      companyName?: string;
      contactName?: string;
      email?: string;
      phone?: string;
      websiteUrl?: string;
      wpWebhookSecret?: string;
      stripeCustomerId?: string;
      accountStatus?: string;
    };
  },
  { ok: true }
>("updateClient");

export const adminSetPortalAccess = call<
  { clientId: string; portalAccess: boolean },
  { ok: true }
>("setPortalAccess");

export const adminDeleteClient = call<
  { clientId: string; deleteStripeCustomer?: boolean },
  { ok: true }
>("deleteClient");

export const adminVerifyClientSite = call<
  { clientId: string },
  {
    ok: boolean;
    reason: string | null;
    expectedStatus: string;
    pluginPreviousStatus: string | null;
    pluginCurrentStatus: string | null;
    driftDetected: boolean;
  }
>("adminVerifyClientSite");

/**
 * One cadence offered for a service (e.g. monthly + annual). Either provide
 * `priceCents` (and let the backend mint a Stripe Price) or `stripePriceId`
 * for a pre-existing one. Sending both is fine — stripePriceId wins.
 */
export interface UpsertPriceOptionInput {
  priceOptionId?: string;
  interval: "day" | "week" | "month" | "year";
  priceCents?: number;
  currency?: string;
  priceDisplay: string;
  badge?: string | null;
  stripePriceId?: string;
}

export interface PriceOption {
  priceOptionId: string;
  interval: "day" | "week" | "month" | "year";
  stripePriceId: string;
  unitAmount: number;
  currency: string;
  priceDisplay: string;
  badge?: string | null;
}

// Admin – services
export const adminUpsertService = call<
  {
    serviceId?: string;
    title: string;
    category: string;
    description: string;
    features: string[];
    serviceType: "one-time" | "recurring" | "bulk";
    /** Optional when createInStripe=true OR prices[] is supplied. */
    stripeProductId?: string;
    /** Optional when createInStripe=true OR prices[] is supplied. */
    stripePriceId?: string;
    priceDisplay: string;
    creditAmount?: number;
    creditType?: "hours" | "tickets";
    isVisible: boolean;
    /** Single-cadence shortcut: backend mints fresh Stripe Product + Price. */
    createInStripe?: boolean;
    /** Whole cents (e.g. 19900 = $199.00). Required when createInStripe. */
    priceCents?: number;
    /** ISO 4217 lowercase. Defaults to "usd". */
    currency?: string;
    /** Required when createInStripe + serviceType=recurring. */
    recurringInterval?: "day" | "week" | "month" | "year";
    /** Multi-cadence pricing — takes precedence over the single-price fields. */
    prices?: UpsertPriceOptionInput[];
    /** Sort key for the customer catalog. Lower = earlier. null clears. */
    displayOrder?: number | null;
  },
  {
    serviceId: string;
    stripeProductId: string;
    stripePriceId: string;
    prices: PriceOption[];
  }
>("upsertService");

export const adminDeleteService = call<
  { serviceId: string },
  { ok: true }
>("deleteService");

export const adminAssignService = call<
  {
    clientId: string;
    serviceId: string;
    status?: "pending_payment" | "active";
    internalNotes?: string;
    /** ISO date "YYYY-MM-DD" — when the service should start (admin reference). */
    startDate?: string | null;
    /** Cadence pre-pick when the service has multiple price options. */
    priceOptionId?: string | null;
  },
  { clientServiceId: string }
>("assignServiceToClient");

export const cancelMyPendingService = call<
  { clientServiceId: string },
  { ok: true }
>("cancelMyPendingService");

// Admin – projects
export const adminCreateProject = call<
  {
    clientId: string;
    projectName: string;
    description?: string;
    phase?: string;
    status?: string;
    assignedTo?: string;
    startDate?: string;
    estimatedCompletionDate?: string;
    recentUpdate?: string;
    clientActionNeeded?: string;
  },
  { projectId: string }
>("createProject");

export const adminUpdateProject = call<
  { projectId: string; patch: Record<string, unknown> },
  { ok: true }
>("updateProject");

export const adminDeleteProject = call<
  { projectId: string },
  { ok: true }
>("deleteProject");

// Support – client + admin
export const submitSupportRequest = call<
  {
    requestType: string;
    priority?: string;
    description: string;
    websiteUrl?: string;
  },
  { requestId: string }
>("submitSupportRequest");

// Admin – create ticket on behalf of a client
export const adminCreateTicket = call<
  {
    clientId: string;
    requestType: string;
    priority?: string;
    description: string;
    websiteUrl?: string;
  },
  { requestId: string }
>("adminCreateTicket");

export const adminUpdateSupportRequest = call<
  {
    requestId: string;
    patch: { status?: string; priority?: string; assignedTo?: string | null };
  },
  { ok: true }
>("updateSupportRequest");

export const addSupportMessage = call<
  {
    requestId: string;
    body: string;
    visibility?: "client" | "admin" | "admin-internal";
  },
  { messageId: string }
>("addSupportMessage");

export const markSupportRequestRead = call<
  { requestId: string },
  { ok: true }
>("markSupportRequestRead");

// Archive / un-archive (soft hide). Sets archived=true so the ticket falls out
// of the default admin list; pass archived=false to restore.
export const adminArchiveSupportRequest = call<
  { requestId: string; archived?: boolean },
  { ok: true; archived: boolean }
>("adminArchiveSupportRequest");

// Hard delete (and cascade subcollections). Destructive — gate behind a
// typed-name confirm in the UI.
export const adminDeleteSupportRequest = call<
  { requestId: string },
  { ok: true; alreadyDeleted?: boolean }
>("adminDeleteSupportRequest");

// Client self-edit
export const updateMyProfile = call<
  {
    patch: {
      companyName?: string;
      contactName?: string;
      email?: string;
      websiteUrl?: string;
    };
  },
  { ok: true }
>("updateMyProfile");

// Admin – Stripe import
export const adminListUnlinkedStripeCustomers = call<
  { limit?: number; startingAfter?: string; query?: string },
  {
    customers: Array<{
      id: string;
      name: string | null;
      email: string | null;
      phone: string | null;
      created: number;
    }>;
    hasMore: boolean;
    nextCursor: string | null;
  }
>("listUnlinkedStripeCustomers");

// Admin – send a test email (smoke-test the SMTP pipeline)
export const adminSendTestEmail = call<
  { to: string },
  { ok: true }
>("adminSendTestEmail");

// Admin – email templates
export type EmailTemplateTrigger =
  | "welcome"
  | "service_assigned"
  | "service_canceled"
  | "invoice_sent"
  | "dunning_day0"
  | "dunning_day3"
  | "dunning_day7"
  | "suspension_warning"
  | "suspended"
  | "reactivated";

export interface EmailTemplate {
  templateId: EmailTemplateTrigger;
  name: string;
  description: string;
  trigger: EmailTemplateTrigger;
  subject: string;
  bodyHtml: string;
  enabled: boolean;
  delayDays: number;
  variables: string[];
}

export const adminListEmailTemplates = call<
  Record<string, never>,
  { templates: EmailTemplate[] }
>("adminListEmailTemplates");

export const adminUpdateEmailTemplate = call<
  {
    templateId: EmailTemplateTrigger;
    patch: Partial<{
      name: string;
      description: string;
      subject: string;
      bodyHtml: string;
      enabled: boolean;
      delayDays: number;
    }>;
  },
  { ok: true }
>("adminUpdateEmailTemplate");

export const adminSeedEmailTemplates = call<
  Record<string, never>,
  { seeded: number; total: number }
>("adminSeedEmailTemplates");

export const adminSendTestEmailTemplate = call<
  { templateId: EmailTemplateTrigger; to: string },
  { ok: true }
>("adminSendTestEmailTemplate");

// Admin – per-client email history + manual sends
export interface ClientEmailLog {
  emailId: string;
  clientId: string;
  templateId: EmailTemplateTrigger;
  subject: string;
  toEmail: string;
  status: "sent" | "failed" | "skipped";
  sentBy: string;
  usedFallback: boolean;
  errorMessage: string | null;
  /** ISO 8601 string. Null only when the row's sentAt was missing. */
  sentAt: string | null;
}

export const adminListClientEmails = call<
  { clientId: string; limit?: number },
  { emails: ClientEmailLog[] }
>("adminListClientEmails");

export const adminSendClientEmail = call<
  {
    clientId: string;
    templateId: EmailTemplateTrigger;
    vars?: Record<string, string>;
  },
  { ok: true }
>("adminSendClientEmail");

export const adminResendClientEmail = call<
  { emailId: string },
  { ok: true }
>("adminResendClientEmail");

// Admin – hide invoice / hide subscription / archive client service
export const adminHideInvoice = call<
  { clientId: string; invoiceId: string; hidden?: boolean },
  { ok: true; hidden: boolean }
>("adminHideInvoice");

export const adminHideSubscription = call<
  { clientId: string; subscriptionId: string; hidden?: boolean },
  { ok: true; hidden: boolean }
>("adminHideSubscription");

export const adminArchiveClientService = call<
  { clientServiceId: string; archived?: boolean },
  { ok: true; archived: boolean }
>("adminArchiveClientService");

// Client – billing badge
export const markBillingRead = call<
  Record<string, never>,
  { ok: true }
>("markBillingRead");

// Domains & Email — manual request workflow
export type DomainRequestType = "register_domain" | "add_mailboxes";

export const submitDomainRequest = call<
  {
    type: DomainRequestType;
    domain: string;
    usernames?: string[];
    notes?: string;
  },
  { requestId: string }
>("submitDomainRequest");

export const cancelMyDomainRequest = call<
  { requestId: string },
  { ok: true }
>("cancelMyDomainRequest");

export const adminApproveDomainRequest = call<
  {
    requestId: string;
    priceUsd?: number;
    sendInvoice?: boolean;
    daysUntilDue?: number;
    invoiceMemo?: string;
  },
  { ok: true; invoiceId: string | null; status: string }
>("adminApproveDomainRequest");

export const adminMarkDomainProvisioned = call<
  { requestId: string; expiryDate: string },
  { ok: true; clientDomainId: string }
>("adminMarkDomainProvisioned");

export const adminMarkMailboxProvisioned = call<
  { requestId: string; username: string; initialPassword?: string },
  { ok: true; clientMailboxId: string; address: string }
>("adminMarkMailboxProvisioned");

export const adminCompleteDomainRequest = call<
  { requestId: string },
  { ok: true }
>("adminCompleteDomainRequest");

export const adminRejectDomainRequest = call<
  { requestId: string; reason: string },
  { ok: true }
>("adminRejectDomainRequest");

// Admin – invoicing
export const adminCreateInvoice = call<
  {
    clientId: string;
    lines: Array<{
      serviceId?: string | null;
      description?: string;
      amount?: number;
      quantity?: number;
    }>;
    memo?: string;
    daysUntilDue?: number;
    sendNow?: boolean;
    /** 0–100 — applied as a one-shot Stripe coupon at finalize time. */
    discountPercent?: number;
  },
  {
    invoiceId: string | null;
    hostedInvoiceUrl: string | null;
    status: string | null;
    subscriptionAssignments: Array<{
      clientServiceId: string;
      serviceId: string;
      serviceName: string;
    }>;
  }
>("createInvoice");
